import React, { createContext, useRef, useState } from "react";

export const GreenMachineContext = createContext();

export const GreenMachineProvider = (props) => {
  const STAGE_NUMBERS = {
    errorReports: -1,
    selectSport: 0,
    selectGameType: 1,
    selectSlate: 2,
    selectSession: 3,
    lineupConfiguration: 4,
    selectPlayers: 5,
    configureRules: 6,
    selectExposures: 7,
    viewLineups: 8,
  };

  const [currentStage, setCurrentStage] = useState(0);

  const [activeSlatesExist, setActiveSlatesExist] = useState(true);

  const [errorMessage, setErrorMessage] = useState(undefined);

  const [greenMachineSessionID, setGreenMachineSessionID] = useState(undefined);
  const [existingLineups, setExistingLineups] = useState(false);

  const [
    greenMachineSessionPositions,
    setGreenMachineSessionPositions,
  ] = useState(undefined);

  const [numFlexSlots, setNumFlexSlots] = useState(undefined);

  const [selectedSportID, setSelectedSportID] = useState(undefined);
  const [selectedSportName, setSelectedSportName] = useState(undefined);

  const [selectedGameTypeID, setSelectedGameTypeID] = useState(undefined);
  const [gameTypeLineupSet, setGameTypeLineupSet] = useState(undefined);

  const [selectedSlateID, setSelectedSlateID] = useState(undefined);

  const [numLineups, setNumLineups] = useState(undefined);
  const [minSalary, setMinSalary] = useState(undefined);
  const [maxSalary, setMaxSalary] = useState(undefined);
  const [playersPerLineup, setPlayersPerLineup] = useState(undefined);

  const [mmaExclude, setMmaExclude] = useState(undefined);
  const [isCptMode, setIsCptMode] = useState(undefined);

  function updateCurrentStage() {
    setCurrentStage(currentStage + 1);
  }

  function jumpToStage(stage) {
    setErrorMessage(undefined);
    setCurrentStage(stage);
  }

  // The active work step registers a save-and-validate handler so that any
  // navigation (Continue CTA or a chevron click) persists its changes first.
  // Only one step is mounted at a time, so a single ref is unambiguous.
  const navGuardRef = useRef(null);
  const navigatingRef = useRef(false);
  const [isNavigating, setIsNavigating] = useState(false);

  function registerStepNavGuard(handler) {
    navGuardRef.current = handler;
  }

  function clearStepNavGuard() {
    navGuardRef.current = null;
  }

  // Run the active step's save+validate (if any), then jump. If the step's
  // saveAndValidate resolves false (validation failed / mutation error) it is
  // responsible for surfacing the error inline, and navigation is cancelled.
  async function requestNavigation(targetStage) {
    if (navigatingRef.current) return;
    if (targetStage === currentStage) return;

    navigatingRef.current = true;
    setIsNavigating(true);
    setErrorMessage(undefined);

    try {
      const guard = navGuardRef.current;
      if (guard && guard.saveAndValidate) {
        const ok = await guard.saveAndValidate(targetStage);
        if (!ok) return;
      }
      setCurrentStage(targetStage);
    } catch (error) {
      console.log(error);
      setErrorMessage("An unexpected error occurred during navigation.");
    } finally {
      navigatingRef.current = false;
      setIsNavigating(false);
    }
  }

  function initializeGreenMachineSession(greenMachineSession) {
    setGreenMachineSessionID(greenMachineSession.id);

    setNumLineups(greenMachineSession.numLineups);

    setMinSalary(greenMachineSession.minSalary);

    setMaxSalary(greenMachineSession.maxSalary);

    setPlayersPerLineup(greenMachineSession.playersPerLineup);

    setExistingLineups(
      greenMachineSession.greenmachinelineupsetSet.edges.length > 0
    );

    let gmPositions = [];
    const rosterSlots =
      greenMachineSession.slate.gameType.gametypelineupsetSet.edges[0].node
        .lineupSet.lineuprosterslotSet.edges;

    setNumFlexSlots(
      rosterSlots
        .filter((slot) => !slot.node.selectable)
        .map((slot) => slot.node.amount)
        .reduce((a, amount) => +a + +amount, 0)
    );

    let writeableRosterSlots = [...rosterSlots];

    writeableRosterSlots
      .sort(function (a, b) {
        return a.node.order - b.node.order;
      })
      .forEach((rosterSlot) => {
        if (rosterSlot.node.selectable) {
          gmPositions[rosterSlot.node.rosterSlot.name] = {
            amount: rosterSlot.node.amount,
            order: rosterSlot.node.order,
          };
        }
      });

    setGreenMachineSessionPositions(gmPositions);
  }

  return (
    <GreenMachineContext.Provider
      value={{
        errorMessage,
        setErrorMessage,
        greenMachineSessionID,
        setGreenMachineSessionID,
        greenMachineSessionPositions,
        setGreenMachineSessionPositions,
        selectedSportID,
        setSelectedSportID,
        selectedSportName,
        setSelectedSportName,
        selectedGameTypeID,
        setSelectedGameTypeID,
        gameTypeLineupSet,
        setGameTypeLineupSet,
        selectedSlateID,
        setSelectedSlateID,
        numLineups,
        setNumLineups,
        minSalary,
        setMinSalary,
        maxSalary,
        setMaxSalary,
        playersPerLineup,
        setPlayersPerLineup,
        currentStage,
        updateCurrentStage,
        jumpToStage,
        requestNavigation,
        registerStepNavGuard,
        clearStepNavGuard,
        isNavigating,
        STAGE_NUMBERS,
        activeSlatesExist,
        setActiveSlatesExist,
        mmaExclude,
        setMmaExclude,
        isCptMode,
        setIsCptMode,
        existingLineups,
        setExistingLineups,
        numFlexSlots,
        setNumFlexSlots,
        initializeGreenMachineSession,
      }}
    >
      {props.children}
    </GreenMachineContext.Provider>
  );
};
